import { useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Style } from "react-style-tag";
import lz from "lzutf8";
import { Editor } from "@craftjs/core";
import { useAppDispatch, useQuery } from "@/hooks";
import RenderNode from "./components/RenderNode";
import ContainerBlock from "@/components/editor/blocks/ContainerBlock";
import TextBlock from "@/components/editor/blocks/TextBlock";
import ImageBlock from "@/components/editor/blocks/ImageBlock";
import VerticalSpacerBlock from "@/components/editor/blocks/VerticalSpacerBlock";
import SectionBlock from "@/components/editor/blocks/section-block/SectionBlock";
import Canvas, { Section } from "./components/Canvas";
import useOrganizations from "@/features/organizations/useOrganizations";
import { fetchOrganizationWebsite, organizationWebsiteSelector } from "../organizations/slice";
import ButtonBlock from "@/components/editor/blocks/ButtonBlock";
import SocialMediaBlock from "@/components/editor/blocks/SocialMediaBlock";
import MapBlock from "@/components/editor/blocks/MapBlock";
import FaqsBlock from "@/components/editor/blocks/FaqsBlock";
import DishBlock from "@/components/editor/blocks/DishBlock";
import ProductDetails from "@/features/product-details/ProductDetails";
import ContactForm from "../organizations/components/contact-form/ContactForm";
import useOrganizationId from "@/hooks/useOrganizationId";
import MenuBlock from "@/components/editor/blocks/MenuBlock";
import CartBlock from "@/components/editor/blocks/CartBlock";
import { addItemToQuote } from "@/store/quoteItemSlice";
import PDPrinting from "../product-details/PDPrinting";
import { useFetchTemplateByIdQuery } from "@/features/templates/templatesApi";
import { Content, Website } from "@/types";
import OffersPopups from "@/components/OffersPopups";
import RegionBlock from "@/components/editor/blocks/RegionBlock";

const WebCanvas = () => {
  const [json, setJson] = useState();
  const [_, setSections] = useState<Section[]>([]);
  const dispatch = useAppDispatch();
  const organizationId = useOrganizationId() || "";
  const { currentOrganization } = useOrganizations() || { currentOrganization: null };

  const websiteQuery = useQuery<Website>(fetchOrganizationWebsite, organizationWebsiteSelector, [organizationId, currentOrganization?.website as string]);

  const template = useFetchTemplateByIdQuery(websiteQuery?.data?.template || skipToken, {
    skip: !websiteQuery?.data?.template,
  });

  const onAdd = (item: Content) => {
    dispatch(addItemToQuote(item));
  };

  useEffect(() => {
    if (websiteQuery?.data?.data || template?.data?.data) {
      const json = lz.decompress(lz.decodeBase64(websiteQuery?.data?.data ?? template?.data?.data ?? ""));
      setJson(json);
    }
  }, [websiteQuery?.data?.data, template?.data?.data]);

  if (websiteQuery.isLoading || template.isLoading) {
    return null;
  }

  if (!json) {
    return null;
  }

  return (
    <>
      <Editor
        resolver={{
          SectionBlock,
          ContainerBlock,
          TextBlock,
          ImageBlock,
          VerticalSpacerBlock,
          ButtonBlock,
          SocialMediaBlock,
          MapBlock,
          FaqsBlock,
          DishBlock,
          MenuBlock,
          CartBlock,
          RegionBlock,
        }}
        enabled={false}
        onRender={RenderNode}
      >
        <Style className={"viewport"} children={websiteQuery.data.styles ? websiteQuery.data.styles : template?.data?.styles ?? ""} />
        <Canvas json={json} setSections={setSections} />
      </Editor>
      {currentOrganization.industry === "food-and-beverages" ? <ProductDetails enableOrders={false} addItem={onAdd} /> : <PDPrinting enableOrders={false} addItem={onAdd} />}
      <ContactForm />
      <OffersPopups />
    </>
  );
};

export default WebCanvas;
